/**
 * PWA install plumbing, shared by every "Install the app" button (main menu,
 * mobile welcome gate, top bar). Chrome/Android hand us a deferred
 * `beforeinstallprompt` we can fire on demand; iOS Safari has no install API
 * at all, so there we show the "Share → Add to Home Screen" walkthrough.
 */

type InstallPrompt = { prompt: () => Promise<void> };

let deferred: InstallPrompt | null = null;
let installed = false;
const listeners = new Map<() => void, Element | undefined>();
const emit = (): void => {
  for (const [f, anchor] of listeners) {
    // screens are rebuilt on every menu visit — drop callbacks whose button
    // is no longer in the DOM instead of accumulating them forever
    if (anchor && !anchor.isConnected) { listeners.delete(f); continue; }
    f();
  }
};

/** Already running as an installed app (home-screen launch)? */
export function isStandalone(): boolean {
  return window.matchMedia("(display-mode: standalone), (display-mode: fullscreen)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;
}

// iPhone/iPod, plus iPads masquerading as desktop Macs (Mac UA + touch)
const IOS = /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (/Macintosh/.test(navigator.userAgent) && (navigator.maxTouchPoints ?? 0) > 1);

window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault(); // we present our own buttons instead of the mini-infobar
  deferred = e as unknown as InstallPrompt;
  if (!isStandalone()) emit();
});
window.addEventListener("appinstalled", () => {
  installed = true;
  deferred = null;
  emit();
});

/** How install can happen right now — null means "no button to show". */
export function installKind(): "native" | "ios" | null {
  if (installed || isStandalone()) return null;
  if (deferred) return "native";
  if (IOS) return "ios";
  return null;
}

/** Buttons subscribe to re-check installKind() when availability changes.
 *  Pass the button as `anchor` so the subscription dies with the DOM node. */
export function onInstallChange(cb: () => void, anchor?: Element): void {
  listeners.set(cb, anchor);
}

/** Fire the install flow: native browser prompt, or the iOS walkthrough. */
export function promptInstall(): void {
  if (deferred) {
    const d = deferred;
    deferred = null; // a BeforeInstallPromptEvent is single-use
    void d.prompt().catch(() => { /* dismissed */ }).finally(emit);
    return;
  }
  if (IOS) showIosHowto();
}

/** iPhone ⛶ tap: explain WHY there is no fullscreen and what to do instead
 *  (distinct from the install sheet — "why does fullscreen do the same as
 *  install" deserved an actual answer on the phone). */
export function showIosFullscreenInfo(): void {
  const ov = document.createElement("div");
  ov.style.cssText = "position:fixed;inset:0;z-index:100001;background:rgba(5,6,10,0.94);" +
    "display:flex;flex-direction:column;align-items:center;justify-content:center;" +
    "gap:1rem;text-align:center;padding:2rem;font-family:monospace";
  ov.innerHTML = `
    <div style="font-size:2.2rem">⛶</div>
    <div style="font-size:1.1rem;color:#00f0ff;letter-spacing:0.15em;text-shadow:0 0 12px rgba(0,240,255,0.8)">FULLSCREEN ON IPHONE</div>
    <div style="color:#e8edf5;max-width:26rem;line-height:1.7">Safari does <b style="color:#ff5577">not allow any website</b> to go fullscreen on iPhone — there is no button we could make work here.<br><br>
    The one way to play with <b style="color:#9dff00">zero browser bars</b>: install NEON PURGE on your home screen and launch it from the icon.</div>`;
  const how = document.createElement("button");
  how.textContent = "📲 SHOW ME HOW";
  how.style.cssText = "background:rgba(157,255,0,0.12);border:1px solid #9dff00;color:#9dff00;" +
    "padding:0.7rem 1.8rem;border-radius:6px;font-family:monospace;letter-spacing:0.1em";
  how.onclick = () => { ov.remove(); showIosHowto(); };
  const ok = document.createElement("button");
  ok.textContent = "OK";
  ok.style.cssText = "background:transparent;border:1px solid #8a95a5;color:#8a95a5;" +
    "padding:0.55rem 2rem;border-radius:6px;font-family:monospace;letter-spacing:0.1em";
  ok.onclick = () => ov.remove();
  ov.append(how, ok);
  document.body.appendChild(ov);
}

function showIosHowto(): void {
  const ov = document.createElement("div");
  ov.style.cssText = "position:fixed;inset:0;z-index:100001;background:rgba(5,6,10,0.94);" +
    "display:flex;flex-direction:column;align-items:center;justify-content:center;" +
    "gap:1rem;text-align:center;padding:2rem;font-family:monospace";
  ov.innerHTML = `
    <div style="font-size:2.4rem">📲</div>
    <div style="font-size:1.2rem;color:#00f0ff;letter-spacing:0.15em;text-shadow:0 0 12px rgba(0,240,255,0.8)">ADD TO HOME SCREEN</div>
    <div style="color:#8a95a5;font-size:0.8rem;max-width:24rem">iPhone only allows fullscreen for installed apps — this is the way</div>
    <div style="color:#e8edf5;max-width:24rem;line-height:1.8;text-align:left">
      1. Tap the <b style="color:#00f0ff">Share</b> button <span style="border:1px solid #00f0ff;border-radius:4px;padding:0 0.35em">⬆</span> in Safari<br>
      2. Choose <b style="color:#00f0ff">Add to Home Screen ➕</b><br>
      3. Launch <b style="color:#9dff00">NEON PURGE</b> from your home screen — true fullscreen, no browser bars</div>`;
  const ok = document.createElement("button");
  ok.textContent = "GOT IT";
  ok.style.cssText = "background:rgba(0,240,255,0.15);border:1px solid #00f0ff;color:#00f0ff;" +
    "padding:0.7rem 2.2rem;border-radius:6px;font-family:monospace;letter-spacing:0.1em";
  ok.onclick = () => ov.remove();
  ov.appendChild(ok);
  document.body.appendChild(ov);
}
